import { Button, Form, Popover } from 'antd';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { connect } from 'react-redux';
import { ExportToCsv } from 'export-to-csv';
import { CSV_EXPORT_DEFAULTS } from '../../utils/DataConstants';
import { UploadOutlined } from '@ant-design/icons';
import UploadWithSampleModal from '../flatfile/UploadWithSampleModal';
import FlatfileImporter from '../flatfile/FlatfileImporter';

export const FLATFILE_HEADER: any[] = [
	{
		label: 'Location ID',
		key: 'id',
		type: 'number',
		constraints: [{ type: 'required' }],
	},
	{
		label: 'Name',
		key: 'name',
		type: 'string',
		alternates: ['Location Name'],
	},
	{
		label: 'Budget',
		key: 'budget',
		type: 'number',
	},
];

const getRecordForBudget = (record = {}) => ({
	id: nullSafeGetOrElse('id', record, ''),
	name: nullSafeGetOrElse('name', record, ''),
	budget: nullSafeGetOrElse('budget', record, 0),
});

const DistinctLocationBudgetComponent: FC<any> = ({
	locationDistinctBudget,
	loading,
	onEditBudget,
	allowedBudget,
	currentUser,
	hidden,
}): React.ReactElement => {
	const [uploadPopupVisible, setUploadPopupVisible] = useState(false);
	const [canOpenEditBudgets, setCanOpenEditBudgets] = useState(false);

	const canEdit = useMemo(() => nullSafeGet('0', locationDistinctBudget), [locationDistinctBudget]);

	const locationDistinctBudgetRecords = useMemo(
		() => locationDistinctBudget.map((_) => getRecordForBudget(_)),
		[locationDistinctBudget]
	);

	const onDownloadSample = useCallback(() => {
		const csvTitle = `location_wise_budget`;
		const csvExporter = new ExportToCsv({
			...CSV_EXPORT_DEFAULTS,
			filename: csvTitle,
			title: csvTitle,
			headers: FLATFILE_HEADER.map((_) => _.label),
		});

		csvExporter.generateCsv(locationDistinctBudgetRecords);
	}, [locationDistinctBudgetRecords]);

	const _onEditBudgetsComplete = () => setCanOpenEditBudgets(false);
	const _onEditBudget = () => setCanOpenEditBudgets(true);

	const showUploadPopup = useCallback(() => {
		setUploadPopupVisible(true);
	}, []);
	const hideUploadPopup = useCallback(() => setUploadPopupVisible(false), []);

	const onDataUpload = useCallback(
		(result) => {
			return new Promise((resolve) => {
				onEditBudget(result.validData);
				resolve('Budget updated successfully!');
				setTimeout(() => setUploadPopupVisible(false), 0);
			});
		},
		[onEditBudget]
	);

	return (
		<Form.Item label="Distinct Location Budget" hidden={hidden}>
			<div className="flex flex-row items-center">
				<Button type="link" disabled={!canEdit} loading={loading} onClick={_onEditBudget}>
					Edit Budgets
				</Button>
				<div className="ml-2">
					<Popover content="Download or Upload distinct location budget" trigger="hover">
						<Button
							size="large"
							className="inline-block-visible-md ml-2"
							icon={<UploadOutlined translate="" />}
							onClick={showUploadPopup}
							loading={loading}
						>
							Upload
						</Button>
					</Popover>
				</div>
			</div>
			{uploadPopupVisible && (
				<UploadWithSampleModal
					type="Distinct Location Budgets"
					title="Upload distinct location budgets"
					downloadText="Do you want to download the existing distinct location budgets?"
					sampleFileText="Download distinct location budget"
					onCancel={hideUploadPopup}
					onDownloadSample={onDownloadSample}
					fields={FLATFILE_HEADER}
					onDataLoad={onDataUpload}
				/>
			)}
			{canOpenEditBudgets ? (
				<FlatfileImporter
					dataLoadComplete={canOpenEditBudgets}
					type="edit_budgets"
					fields={FLATFILE_HEADER}
					data={locationDistinctBudgetRecords}
					onComplete={_onEditBudgetsComplete}
					onDataLoad={onDataUpload}
				/>
			) : null}
		</Form.Item>
	);
};

const mapStateToProps = (state) => ({
	currentUser: state.session.currentUser,
});

export default connect(mapStateToProps)(DistinctLocationBudgetComponent);
